import { useState } from 'react';
import { Card, Button, Modal, Input, Textarea, Badge } from '../components/Common';
import { usePatients, useUpdateEnrichment } from '../hooks/usePatients';
import { usePhysios } from '../hooks/usePhysios';
import {
  useRequirementTags,
  usePatientRequirements,
  useAddPatientRequirement,
  useRemovePatientRequirement,
  useCreateRequirementTag,
} from '../hooks/useRequirementTags';
import type { Patient, UpdateEnrichmentInput, BlockedHours, GenderPreference } from '../types';

const SPECIALISMS = [
  'Neurological',
  'Stroke Recovery',
  'Elderly Care',
  "Parkinson's",
  'Mobility Assessment',
];

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const HOURS = Array.from({ length: 12 }, (_, i) => i + 7); // 7am to 6pm

type DayName = (typeof DAYS)[number];

function BlockedHoursCalendar({
  blockedHours,
  onChange,
}: {
  blockedHours: BlockedHours;
  onChange: (hours: BlockedHours) => void;
}) {
  const toggleHour = (day: DayName, hour: number) => {
    const dayHours = blockedHours[day] || [];
    const newDayHours = dayHours.includes(hour)
      ? dayHours.filter((h) => h !== hour)
      : [...dayHours, hour].sort((a, b) => a - b);

    onChange({
      ...blockedHours,
      [day]: newDayHours.length > 0 ? newDayHours : undefined,
    });
  };

  const isBlocked = (day: DayName, hour: number) => {
    return blockedHours[day]?.includes(hour) || false;
  };

  const formatHour = (hour: number) => {
    if (hour === 12) return '12pm';
    if (hour > 12) return `${hour - 12}pm`;
    return `${hour}am`;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="p-1 text-left font-medium text-gray-500"></th>
            {DAYS.map((day) => (
              <th key={day} className="p-1 text-center font-medium text-gray-500 capitalize">
                {day.slice(0, 3)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {HOURS.map((hour) => (
            <tr key={hour}>
              <td className="p-1 text-right text-gray-500 pr-2">{formatHour(hour)}</td>
              {DAYS.map((day) => (
                <td key={`${day}-${hour}`} className="p-0.5">
                  <button
                    type="button"
                    onClick={() => toggleHour(day, hour)}
                    className={`w-full h-6 rounded transition-colors ${
                      isBlocked(day, hour)
                        ? 'bg-red-400 hover:bg-red-500'
                        : 'bg-green-100 hover:bg-green-200'
                    }`}
                    title={`${formatHour(hour)} ${day}: ${isBlocked(day, hour) ? 'Blocked' : 'Available'}`}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-2 text-xs text-gray-500">
        <span className="inline-block w-3 h-3 bg-green-100 rounded mr-1 align-middle"></span>
        Available
        <span className="inline-block w-3 h-3 bg-red-400 rounded mx-1 ml-3 align-middle"></span>
        Blocked (click to toggle)
      </p>
    </div>
  );
}

function PatientCard({
  patient,
  onEdit,
}: {
  patient: Patient;
  onEdit: (patient: Patient) => void;
}) {
  return (
    <div
      className="p-4 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onEdit(patient)}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0 flex-1">
          <div className="font-medium text-gray-900">{patient.name}</div>
          <div className="text-sm text-gray-500">{patient.wuid}</div>
          {patient.postcode && (
            <div className="text-sm text-gray-500 mt-1">{patient.postcode}</div>
          )}
        </div>
        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
      <div className="mt-3 flex flex-wrap gap-1">
        {patient.enrichment?.specialisms_needed.map((spec) => (
          <Badge key={spec} variant="info">
            {spec}
          </Badge>
        ))}
        {patient.requirements?.map((req) => (
          <Badge key={`req-${req.id}`} variant="default">
            {req.name}
          </Badge>
        ))}
        {patient.enrichment?.gender_preference === 'male' && (
          <Badge variant="warning">Male pref</Badge>
        )}
        {patient.enrichment?.gender_preference === 'female' && (
          <Badge variant="warning">Female pref</Badge>
        )}
        {patient.enrichment?.carer_schedule && (
          <Badge variant="info">Carer</Badge>
        )}
        {(!patient.enrichment?.specialisms_needed?.length && !patient.requirements?.length && !patient.enrichment?.gender_preference) && (
          <span className="text-gray-400 text-sm">No constraints set</span>
        )}
      </div>
    </div>
  );
}

function PatientRow({
  patient,
  onEdit,
}: {
  patient: Patient;
  onEdit: (patient: Patient) => void;
}) {
  return (
    <tr
      className="hover:bg-gray-50 cursor-pointer transition-colors"
      onClick={() => onEdit(patient)}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="font-medium text-gray-900">{patient.name}</div>
        <div className="text-sm text-gray-500">{patient.wuid}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {patient.postcode || '-'}
      </td>
      <td className="px-6 py-4">
        <div className="flex flex-wrap gap-1">
          {patient.enrichment?.specialisms_needed.map((spec) => (
            <Badge key={spec} variant="info">
              {spec}
            </Badge>
          )) || <span className="text-gray-400 text-sm">None set</span>}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {patient.enrichment?.gender_preference === 'male' && (
          <Badge variant="warning">Male preferred</Badge>
        )}
        {patient.enrichment?.gender_preference === 'female' && (
          <Badge variant="warning">Female preferred</Badge>
        )}
        {patient.enrichment?.carer_schedule && (
          <Badge variant="info">Has carer</Badge>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-400">
        Edit
      </td>
    </tr>
  );
}

export function PatientManagement() {
  const { data: patients, isLoading } = usePatients();
  const { data: physios } = usePhysios();
  const { data: requirementTags } = useRequirementTags();
  const updateEnrichment = useUpdateEnrichment();
  const addRequirement = useAddPatientRequirement();
  const removeRequirement = useRemovePatientRequirement();
  const createTag = useCreateRequirementTag();

  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState<UpdateEnrichmentInput>({});
  const [showNewTagModal, setShowNewTagModal] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [newTagPhysioIds, setNewTagPhysioIds] = useState<number[]>([]);
  const [createTagError, setCreateTagError] = useState<string | null>(null);

  // Fetch requirements for the patient being edited
  const { data: patientRequirements } = usePatientRequirements(editingPatient?.id || 0);

  const filteredPatients = patients?.filter(
    (p) =>
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.wuid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.postcode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleEditClick = (patient: Patient) => {
    setEditingPatient(patient);
    setFormData({
      specialisms_needed: patient.enrichment?.specialisms_needed || [],
      gender_preference: patient.enrichment?.gender_preference || 'none',
      can_climb_stairs: patient.enrichment?.can_climb_stairs ?? true,
      min_experience_level: patient.enrichment?.min_experience_level || 1,
      preferred_physios: patient.enrichment?.preferred_physios || [],
      blocked_hours: patient.enrichment?.blocked_hours || {},
      notes: patient.enrichment?.notes || '',
    });
  };

  const handleSave = async () => {
    if (!editingPatient) return;

    try {
      await updateEnrichment.mutateAsync({
        id: editingPatient.id,
        data: formData,
      });
      setEditingPatient(null);
    } catch (error) {
      console.error('Failed to update enrichment:', error);
    }
  };

  const toggleSpecialism = (spec: string) => {
    const current = formData.specialisms_needed || [];
    if (current.includes(spec)) {
      setFormData({
        ...formData,
        specialisms_needed: current.filter((s) => s !== spec),
      });
    } else {
      setFormData({
        ...formData,
        specialisms_needed: [...current, spec],
      });
    }
  };

  const togglePreferredPhysio = (physioId: number) => {
    const current = formData.preferred_physios || [];
    if (current.includes(physioId)) {
      setFormData({
        ...formData,
        preferred_physios: current.filter((id) => id !== physioId),
      });
    } else {
      setFormData({
        ...formData,
        preferred_physios: [...current, physioId],
      });
    }
  };

  const openNewTagModal = () => {
    // Default to all physios selected
    setNewTagPhysioIds(physios?.map((p) => p.id) || []);
    setNewTagName('');
    setNewTagDescription('');
    setCreateTagError(null);
    setShowNewTagModal(true);
  };

  const toggleNewTagPhysio = (physioId: number) => {
    if (newTagPhysioIds.includes(physioId)) {
      setNewTagPhysioIds(newTagPhysioIds.filter((id) => id !== physioId));
    } else {
      setNewTagPhysioIds([...newTagPhysioIds, physioId]);
    }
  };

  const handleCreateAndAddTag = async () => {
    if (!newTagName.trim() || !editingPatient) return;

    setCreateTagError(null);
    try {
      const result = await createTag.mutateAsync({
        name: newTagName.trim(),
        description: newTagDescription.trim() || undefined,
        capable_physio_ids: newTagPhysioIds,
      });

      // Auto-add the new requirement to the patient
      if (result.data?.id) {
        await addRequirement.mutateAsync({
          patientId: editingPatient.id,
          tagId: result.data.id
        });
      }

      setShowNewTagModal(false);
      setNewTagName('');
      setNewTagDescription('');
      setNewTagPhysioIds([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create requirement';
      setCreateTagError(message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Patient Management</h1>
      </div>

      <Card>
        <div className="mb-4">
          <Input
            placeholder="Search patients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {isLoading ? (
          <p className="text-gray-500">Loading patients...</p>
        ) : !filteredPatients || filteredPatients.length === 0 ? (
          <p className="text-gray-500">No patients found</p>
        ) : (
          <>
            {/* Mobile: Card view */}
            <div className="md:hidden space-y-3">
              {filteredPatients.map((patient) => (
                <PatientCard key={patient.id} patient={patient} onEdit={handleEditClick} />
              ))}
            </div>

            {/* Desktop: Table view */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Patient
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Postcode
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Specialisms Needed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Constraints
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPatients.map((patient) => (
                    <PatientRow key={patient.id} patient={patient} onEdit={handleEditClick} />
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editingPatient}
        onClose={() => setEditingPatient(null)}
        title={`Edit Patient: ${editingPatient?.name}`}
        size="lg"
      >
        <div className="space-y-6">
          {/* Specialisms */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Specialisms Needed
            </label>
            <div className="flex flex-wrap gap-2">
              {SPECIALISMS.map((spec) => (
                <button
                  key={spec}
                  type="button"
                  onClick={() => toggleSpecialism(spec)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    formData.specialisms_needed?.includes(spec)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {spec}
                </button>
              ))}
            </div>
          </div>

          {/* Gender Preference */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Physio Gender Preference
            </label>
            <select
              value={formData.gender_preference || 'none'}
              onChange={(e) =>
                setFormData({ ...formData, gender_preference: e.target.value as GenderPreference })
              }
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              <option value="none">No preference</option>
              <option value="male">Male preferred</option>
              <option value="female">Female preferred</option>
            </select>
          </div>

          {/* Other Constraints */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Other Constraints</label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.can_climb_stairs ?? true}
                onChange={(e) =>
                  setFormData({ ...formData, can_climb_stairs: e.target.checked })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Physio can climb stairs at location</span>
            </label>
          </div>

          {/* Requirements */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patient Requirements
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {patientRequirements?.map((req) => (
                <span
                  key={req.id}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800"
                >
                  {req.name}
                  <button
                    type="button"
                    onClick={() => {
                      if (editingPatient) {
                        removeRequirement.mutate({ patientId: editingPatient.id, tagId: req.id });
                      }
                    }}
                    className="ml-2 text-purple-600 hover:text-purple-800"
                  >
                    &times;
                  </button>
                </span>
              ))}
              {(!patientRequirements || patientRequirements.length === 0) && (
                <span className="text-gray-400 text-sm">No requirements set</span>
              )}
            </div>

            {/* Add requirement dropdown */}
            <div className="flex gap-2">
              <select
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-sm"
                defaultValue=""
                onChange={(e) => {
                  const tagId = parseInt(e.target.value, 10);
                  if (tagId && editingPatient) {
                    addRequirement.mutate({ patientId: editingPatient.id, tagId });
                    e.target.value = '';
                  }
                }}
              >
                <option value="">Add existing requirement...</option>
                {requirementTags
                  ?.filter((tag) => !patientRequirements?.some((r) => r.id === tag.id))
                  .map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
              </select>
              <button
                type="button"
                onClick={openNewTagModal}
                className="px-3 py-2 text-sm text-primary-600 hover:text-primary-800 hover:bg-primary-50 rounded whitespace-nowrap"
              >
                + New Requirement
              </button>
            </div>

            <p className="mt-2 text-xs text-gray-500">
              Requirements help match physios with the right capabilities. Physios without required capabilities will rank lower in suggestions.
            </p>
          </div>

          {/* Minimum Experience Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Physio Experience Level
            </label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({ ...formData, min_experience_level: level })}
                  className="p-1 hover:scale-110 transition-transform"
                >
                  <svg
                    className={`w-8 h-8 ${
                      level <= (formData.min_experience_level || 1)
                        ? 'text-amber-400'
                        : 'text-gray-200 hover:text-amber-200'
                    }`}
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              ))}
              <span className="ml-2 text-sm text-gray-500">
                {formData.min_experience_level || 1} / 5
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Only physios with at least this experience level will be suggested
            </p>
          </div>

          {/* Preferred Physios */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Preferred Physios
            </label>
            <div className="flex flex-wrap gap-2">
              {physios?.map((physio) => (
                <button
                  key={physio.id}
                  type="button"
                  onClick={() => togglePreferredPhysio(physio.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    formData.preferred_physios?.includes(physio.id)
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {physio.name}
                </button>
              ))}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              If preferred physios are set, only they will be suggested for this patient
            </p>
          </div>

          {/* Blocked Hours */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Blocked Hours (when patient is NOT available)
            </label>
            <BlockedHoursCalendar
              blockedHours={formData.blocked_hours || {}}
              onChange={(hours) => setFormData({ ...formData, blocked_hours: hours })}
            />
          </div>

          {/* Notes */}
          <Textarea
            label="Notes"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder="Any additional notes about this patient..."
          />

          {/* Actions */}
          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button variant="secondary" onClick={() => setEditingPatient(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} isLoading={updateEnrichment.isPending}>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>

      {/* New Requirement Modal */}
      <Modal
        isOpen={showNewTagModal}
        onClose={() => setShowNewTagModal(false)}
        title="Create New Requirement"
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Requirement Title"
            value={newTagName}
            onChange={(e) => {
              setNewTagName(e.target.value);
              setCreateTagError(null);
            }}
            placeholder="e.g., Hoist Training, Manual Handling..."
            required
          />

          <Textarea
            label="Description (optional)"
            value={newTagDescription}
            onChange={(e) => setNewTagDescription(e.target.value)}
            placeholder="Describe what this requirement means..."
            rows={2}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capable Physios
            </label>
            <p className="text-xs text-gray-500 mb-3">
              All physios are selected by default. Deselect any physios who cannot fulfill this requirement.
            </p>
            <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-2 border rounded-md bg-gray-50">
              {physios?.map((physio) => (
                <button
                  key={physio.id}
                  type="button"
                  onClick={() => toggleNewTagPhysio(physio.id)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    newTagPhysioIds.includes(physio.id)
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-200 text-gray-500 line-through'
                  }`}
                >
                  {physio.name}
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">
              {newTagPhysioIds.length} of {physios?.length || 0} physios can fulfill this requirement
            </p>
          </div>

          {createTagError && (
            <p className="text-sm text-red-600" role="alert">
              {createTagError}
            </p>
          )}

          <div className="flex justify-end space-x-2 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={() => setShowNewTagModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAndAddTag}
              isLoading={createTag.isPending || addRequirement.isPending}
              disabled={!newTagName.trim()}
            >
              Create & Add to Patient
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
